// Session tracking + API call
// - Stores tracking data in sessionStorage
// - Updates visited pages per session
// - Sends data to {apiUrl}/track
// - Sets organic promo code (apd10) for non-campaign traffic

import apiUrl from "../config/apiUrl";
import apiKey from "../config/apiKey";

const TRACKING_STORAGE_KEY = "hanger_tracking";
const PROMO_CODE_STORAGE_KEY = "hanger_promo_code";
const PROMO_CODE_VALUE = "apd10";

const isBrowser = typeof window !== "undefined";

const generateSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return (
    Date.now().toString(36) + Math.random().toString(36).substring(2, 10)
  );
};

const getDevice = () => {
  if (!isBrowser) return "Unknown";
  const ua = navigator.userAgent || "";
  return /Mobi|Android|iPhone|iPad/i.test(ua) ? "Mobile" : "Desktop";
};

const getBrowser = () => {
  if (!isBrowser) return "Unknown";
  const ua = navigator.userAgent || "";
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("OPR/") || ua.includes("Opera")) return "Opera";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
  if (ua.includes("Firefox/")) return "Firefox";
  return "Unknown";
};

const getScreenSize = () => {
  if (!isBrowser) return "";
  return `${window.screen.width}x${window.screen.height}`;
};

const getInitialUtmAndSource = (currentUrl) => {
  const params = currentUrl.searchParams;

  // Get UTM from query
  let utm_source = params.get("utm_source") || "";
  let utm_medium = params.get("utm_medium") || "";
  let utm_campaign = params.get("utm_campaign") || "";
  let utm_term = params.get("utm_term") || "";
  let utm_content = params.get("utm_content") || "";

  if (!utm_source && isBrowser && currentUrl.hash.includes("utm_")) {
    const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ""));
    utm_source = hashParams.get("utm_source") || utm_source;
    utm_medium = hashParams.get("utm_medium") || utm_medium;
    utm_campaign = hashParams.get("utm_campaign") || utm_campaign;
    utm_term = hashParams.get("utm_term") || utm_term;
    utm_content = hashParams.get("utm_content") || utm_content;
  }

  const rawReferrer = isBrowser ? document.referrer || "" : "";

  const hasUtmSource = utm_source.trim() !== "";
  const hasUtmMedium = utm_medium.trim() !== "";

  if (!hasUtmSource && !hasUtmMedium) {
    utm_source = "organic";
    utm_medium = "organic";
  } else {
    utm_source = utm_source.toLowerCase();
    utm_medium = utm_medium.toLowerCase();
  }

  // Referrer logic
  let referrer = "direct";
  if (utm_source) referrer = utm_source;
  else if (utm_medium) referrer = utm_medium;
  else if (rawReferrer) {
    try {
      referrer = new URL(rawReferrer).hostname.toLowerCase();
    } catch {
      referrer = rawReferrer.toLowerCase();
    }
  }

  return {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_term,
    utm_content,
    referrer,
  };
};

// Promo code for non-campaign traffic
const savePromoCodeIfNotCampaign = (utm_medium, utm_campaign) => {
  if (!isBrowser) return;

  const medium = (utm_medium || "").toLowerCase().trim();
  const campaign = (utm_campaign || "").toLowerCase().trim();

  const nonCampaignMediums = [
    "",
    "organic",
    "(organic)",
    "seo",
    "referral",
    "direct",
    "(not set)",
  ];

  if (campaign) return; // campaign traffic → no promo
  if (!nonCampaignMediums.includes(medium)) return; // real campaign medium → no promo

  window.sessionStorage.setItem(PROMO_CODE_STORAGE_KEY, PROMO_CODE_VALUE);
};

export const loadTrackingData = () => {
  if (!isBrowser) return null;
  try {
    const raw = window.sessionStorage.getItem(TRACKING_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const fetchIpAddress = async () => {
  if (!isBrowser) return "";
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip || "";
  } catch {
    return "";
  }
};

const persistTrackingData = (data) => {
  if (!isBrowser) return;
  window.sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(data));
};

const postTrackingData = async (data) => {
  try {
    await fetch(`${apiUrl}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: data.session_id,
        ip_address: data.ip_address,
        reference_no: data.reference_no,
        device: data.device,
        browser: data.browser,
        screen: data.screen,
        utm_source: data.utm_source,
        utm_medium: data.utm_medium,
        utm_campaign: data.utm_campaign,
        utm_term: data.utm_term,
        utm_content: data.utm_content,
        page: data.page,
        referrer: data.referrer,
        key: apiKey
      }),
    });
  } catch (error) {
    console.error("Tracking API Error:", error);
  }
};

export const trackPageView = async (url) => {
  if (!isBrowser) return;

  const currentUrl = new URL(url, window.location.origin);
  const currentPath = currentUrl.pathname + currentUrl.search;

  let data = loadTrackingData();
  const referenceNoFromUrl = currentUrl.searchParams.get("reference_no") || "";

  if (!data) {
    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      referrer,
    } = getInitialUtmAndSource(currentUrl);

    savePromoCodeIfNotCampaign(utm_medium, utm_campaign);

    const ip_address = await fetchIpAddress();

    data = {
      session_id: generateSessionId(),
      ip_address,
      reference_no: referenceNoFromUrl || "",
      device: getDevice(),
      browser: getBrowser(),
      screen: getScreenSize(),
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      page: currentPath,
      referrer,
      visitedPages: [currentPath],
    };
  } else {
    data.reference_no = referenceNoFromUrl || data.reference_no || "";
    if (!data.ip_address) data.ip_address = await fetchIpAddress();

    data.page = currentPath;
    if (!data.visitedPages.includes(currentPath)) {
      data.visitedPages.push(currentPath);
    }
  }

  persistTrackingData(data);

  // Delay the tracking API call by 5 seconds
  setTimeout(() => {
    postTrackingData(data);
  }, 5000);
};

export const updateTrackingReferenceFromBooking = (bookingResponse) => {
  if (!isBrowser || !bookingResponse) return;

  const data = loadTrackingData();
  if (!data) return;

  try {
    const apiData = bookingResponse.data || {};
    const arr = apiData.reference_no;
    if (Array.isArray(arr) && arr.length) {
      data.reference_no = arr[0];
    }
    persistTrackingData(data);
  } catch {
    // ignore
  }
};

export const getStoredPromoCode = () => {
  if (!isBrowser) return null;
  return window.sessionStorage.getItem(PROMO_CODE_STORAGE_KEY);
};

export { PROMO_CODE_STORAGE_KEY, PROMO_CODE_VALUE };
